import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { LayoutGrid, CalendarDays, Calendar, MessageSquare, Users, FileText, Package, Image, Star, HelpCircle, Clock, Settings, LogOut, ExternalLink, Film, Instagram } from "lucide-react";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { LOGO_URL } from "@/lib/constants";
import { Panel } from "@/pages/admin/adminui";
import BookingsTab from "@/pages/admin/BookingsTab";
import { QuotesTab, ClientsTab, MessagesTab, CalendarTab } from "@/pages/admin/ContentTabs";
import { PackagesTab, MediaTab, TestimonialsTab, FaqTab, AvailabilityTab, SettingsTab, ProjectsTab, InstagramTab } from "@/pages/admin/ManageTabs";

const TABS = [
  { id: "overview", label: "Overview", icon: LayoutGrid },
  { id: "bookings", label: "Bookings", icon: CalendarDays },
  { id: "calendar", label: "Calendar", icon: Calendar },
  { id: "quotes", label: "Quotes", icon: FileText },
  { id: "clients", label: "Clients", icon: Users },
  { id: "messages", label: "Messages", icon: MessageSquare },
  { id: "packages", label: "Packages", icon: Package },
  { id: "media", label: "Media", icon: Image },
  { id: "projects", label: "Projects", icon: Film },
  { id: "instagram", label: "Instagram", icon: Instagram },
  { id: "testimonials", label: "Testimonials", icon: Star },
  { id: "faq", label: "FAQ", icon: HelpCircle },
  { id: "availability", label: "Availability", icon: Clock },
  { id: "settings", label: "Settings", icon: Settings },
];

const STAT_CARDS = [
  ["today", "Bookings Today"], ["week", "This Week"], ["month", "This Month"],
  ["pending", "Pending Requests"], ["confirmed", "Confirmed Shoots"],
  ["quotes", "Quote Requests"], ["clients", "Total Clients"], ["unread_messages", "Unread Messages"],
];

function Overview({ onGo }) {
  const [stats, setStats] = useState({});
  useEffect(() => { api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {}); }, []);
  return (
    <div>
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4" data-testid="overview-stats">
        {STAT_CARDS.map(([k, l], i) => (
          <motion.div key={k} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
            className="rounded-2xl border border-white/10 bg-[#12141C] p-5" data-testid={`stat-${k}`}>
            <p className="font-display font-bold text-4xl text-white">{stats[k] ?? 0}</p>
            <p className="text-[#64748B] text-xs font-mono2 uppercase tracking-wide mt-1">{l}</p>
          </motion.div>
        ))}
      </div>
      <div className="mt-6 grid sm:grid-cols-2 gap-4">
        <Panel title="Quick Actions">
          <div className="flex flex-wrap gap-2">
            {[["bookings", "View Bookings"], ["calendar", "Open Calendar"], ["media", "Upload Photos"], ["packages", "Edit Packages"]].map(([id, l]) => (
              <button key={id} onClick={() => onGo(id)} className="text-xs font-mono2 px-4 py-2.5 rounded-full border border-white/15 text-white/70 hover:border-[#02AACF] hover:text-[#02AACF] transition-all" data-testid={`quick-${id}`}>{l}</button>
            ))}
          </div>
        </Panel>
        <Panel title="Total Bookings">
          <p className="font-display font-bold text-6xl text-[#02AACF]">{stats.total ?? 0}</p>
          <p className="text-[#64748B] text-sm mt-1">All-time booking requests received.</p>
        </Panel>
      </div>
    </div>
  );
}

export default function AdminDashboard() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("overview");

  const doLogout = async () => { await logout(); navigate("/"); };

  return (
    <div className="min-h-screen bg-[#090A0F] flex flex-col lg:flex-row">
      {/* Sidebar */}
      <aside className="lg:w-64 lg:h-screen lg:sticky top-0 border-b lg:border-b-0 lg:border-r border-white/10 bg-[#0b0d12] flex flex-col shrink-0">
        <div className="p-5 border-b border-white/10 flex items-center justify-between">
          <img src={LOGO_URL} alt="LUHAI" className="h-7" />
          <span className="font-mono2 text-[10px] tracking-[0.2em] text-[#02AACF] uppercase">Admin</span>
        </div>
        <nav className="flex lg:flex-col gap-1 p-3 overflow-x-auto lg:overflow-y-auto no-scrollbar flex-1">
          {TABS.map((t) => (
            <button key={t.id} onClick={() => setTab(t.id)} data-testid={`tab-${t.id}`}
              className={`flex items-center gap-3 px-4 py-2.5 rounded-xl text-sm whitespace-nowrap transition-all ${tab === t.id ? "bg-[#02AACF]/15 text-[#02AACF]" : "text-white/60 hover:text-white hover:bg-white/5"}`}>
              <t.icon size={17} /> {t.label}
            </button>
          ))}
        </nav>
        <div className="p-3 border-t border-white/10 hidden lg:block">
          <div className="flex items-center gap-2 px-2 mb-2">
            {user?.picture && <img src={user.picture} alt="" className="h-7 w-7 rounded-full" />}
            <span className="text-xs text-[#94A3B8] truncate">{user?.email}</span>
          </div>
          <button onClick={() => window.open("/", "_blank")} className="flex items-center gap-2 px-4 py-2 text-xs text-white/60 hover:text-white w-full" data-testid="view-site"><ExternalLink size={14} /> View site</button>
          <button onClick={doLogout} className="flex items-center gap-2 px-4 py-2 text-xs text-[#f87171] hover:text-[#ef4444] w-full" data-testid="admin-logout"><LogOut size={14} /> Logout</button>
        </div>
      </aside>

      {/* Content */}
      <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-x-hidden">
        <div className="flex items-center justify-between mb-6">
          <h1 className="font-display font-bold text-white text-2xl sm:text-3xl capitalize">{TABS.find((t) => t.id === tab)?.label}</h1>
          <button onClick={doLogout} className="lg:hidden text-[#f87171] p-2" data-testid="admin-logout-mobile"><LogOut size={18} /></button>
        </div>
        {tab === "overview" && <Overview onGo={setTab} />}
        {tab === "bookings" && <BookingsTab />}
        {tab === "calendar" && <CalendarTab />}
        {tab === "quotes" && <QuotesTab />}
        {tab === "clients" && <ClientsTab />}
        {tab === "messages" && <MessagesTab />}
        {tab === "packages" && <PackagesTab />}
        {tab === "media" && <MediaTab />}
        {tab === "projects" && <ProjectsTab />}
        {tab === "instagram" && <InstagramTab />}
        {tab === "testimonials" && <TestimonialsTab />}
        {tab === "faq" && <FaqTab />}
        {tab === "availability" && <AvailabilityTab />}
        {tab === "settings" && <SettingsTab />}
      </main>
    </div>
  );
}
